"use client";

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

type WorkerStatus = {
  worker?: {
    status?: string;
    alive?: boolean;
    pid?: number | null;
    message?: string;
    current?: {
      kind?: string;
      target?: string;
      author?: string;
      desc?: string;
    } | null;
    last_finished?: {
      kind?: string;
      status?: string;
      message?: string;
      at?: string;
    };
    summary?: Record<string, number>;
  };
  queue?: {
    pending?: number;
    running?: number;
    done?: number;
    failed?: number;
    skipped?: number;
    total?: number;
    current?: {
      kind?: string;
      target?: string;
      author?: string;
      message?: string;
      status?: string;
    } | null;
    recent?: Array<{
      id: string;
      kind: string;
      target: string;
      author?: string;
      status: string;
      message?: string;
    }>;
  };
  asr?: {
    status?: string;
    done?: number;
    total?: number;
    failed?: number;
    percent?: number;
    model?: string;
    current?: { path?: string; index?: number; total?: number; author?: string };
  };
};

export default function JobsPage() {
  const [data, setData] = useState<WorkerStatus | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const res = await fetch("/api/worker/status", { cache: "no-store" });
    setData(await res.json());
  }, []);

  useEffect(() => {
    queueMicrotask(() => void refresh());
    const t = window.setInterval(() => void refresh(), 1200);
    return () => window.clearInterval(t);
  }, [refresh]);

  async function start(prefer?: "asr" | "download") {
    setBusy(true);
    try {
      const res = await fetch("/api/worker/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prefer }),
      });
      const j = await res.json();
      if (!res.ok) toast.error(String(j.error || "启动失败"));
      else toast.success(String(j.message || "worker 已启动"));
      await refresh();
    } catch (e) {
      toast.error(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function stop() {
    setBusy(true);
    try {
      await fetch("/api/worker/stop", { method: "POST" });
      toast.message("已请求停止 worker");
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  const q = data?.queue;
  const w = data?.worker;
  const asr = data?.asr;
  const finished =
    (q?.done || 0) + (q?.failed || 0) + (q?.skipped || 0);
  const total = q?.total || 0;
  const pct = total ? Math.round((finished / total) * 100) : 0;
  const asrPct =
    asr?.total && asr.total > 0
      ? Math.round((((asr.done || 0) + (asr.failed || 0)) / asr.total) * 100)
      : 0;

  const cur = w?.current || q?.current;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">任务中心</h1>
        <p className="mt-1 text-sm text-zinc-500">
          后台 worker 处理下载/ASR/LLM，会话不必等待。本页 1.2s 轮询状态。
        </p>
      </header>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy || w?.alive}
          onClick={() => void start()}
          className="rounded-lg bg-emerald-600 px-4 py-2 text-sm text-white disabled:opacity-50"
        >
          启动 Worker
        </button>
        <button
          type="button"
          disabled={busy || w?.alive}
          onClick={() => void start("asr")}
          className="rounded-lg border border-violet-500/40 bg-violet-500/10 px-4 py-2 text-sm text-violet-200 disabled:opacity-50"
        >
          启动（优先 ASR）
        </button>
        <button
          type="button"
          disabled={busy || !w?.alive}
          onClick={() => void stop()}
          className="rounded-lg border border-red-500/40 px-4 py-2 text-sm text-red-300 disabled:opacity-50"
        >
          停止 Worker
        </button>
      </div>

      <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4 space-y-3">
        <div className="flex justify-between text-xs">
          <span className="text-zinc-300">
            Worker：{w?.alive ? "运行中" : "未运行"}
            {w?.pid ? ` · pid ${w.pid}` : ""}
          </span>
          <span className="text-emerald-300 tabular-nums">{pct}%</span>
        </div>
        <div className="h-2.5 rounded-full bg-zinc-800 overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center text-[11px]">
          {(
            [
              ["待处理", q?.pending, "text-amber-300"],
              ["运行中", q?.running, "text-sky-400"],
              ["完成", q?.done, "text-emerald-400"],
              ["失败", q?.failed, "text-red-400"],
              ["跳过", q?.skipped, "text-zinc-500"],
              ["总计", q?.total, "text-zinc-300"],
            ] as const
          ).map(([k, v, c]) => (
            <div key={k} className="rounded-lg border border-zinc-800 px-2 py-2">
              <div className="text-zinc-500">{k}</div>
              <div className={`text-lg font-semibold tabular-nums ${c}`}>{v ?? 0}</div>
            </div>
          ))}
        </div>
        <div className="rounded-lg border border-sky-500/20 bg-sky-500/5 px-3 py-2 text-xs text-sky-100">
          <div className="text-[10px] text-sky-300/80">当前任务</div>
          {cur ? (
            <div className="mt-1">
              <span className="text-sky-300">[{cur.kind}]</span> {cur.author}{" "}
              <span className="font-mono text-zinc-400">
                {(cur.target || "").slice(0, 36)}
              </span>
              <div className="text-zinc-500 line-clamp-2">{(cur as {desc?: string; message?: string}).desc || (cur as {desc?: string; message?: string}).message}</div>
            </div>
          ) : (
            <div className="mt-1 text-zinc-500">空闲</div>
          )}
        </div>
        {w?.last_finished && (
          <p className="text-[11px] text-zinc-500">
            上一条：{w.last_finished.kind} · {w.last_finished.status} ·{" "}
            {(w.last_finished.message || "").slice(0, 60)}
          </p>
        )}
      </div>

      <div className="rounded-xl border border-violet-500/25 bg-violet-500/5 p-4 space-y-2">
        <div className="flex justify-between text-xs text-violet-200">
          <span>ASR 批量 {asr?.model || ""} · {asr?.status || "idle"}</span>
          <span className="tabular-nums">{asrPct}%</span>
        </div>
        <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
          <div className="h-full bg-violet-500" style={{ width: `${asrPct}%` }} />
        </div>
        <p className="text-[11px] text-zinc-500">
          {asr?.current
            ? `正在 ${asr.current.index}/${asr.current.total} ${asr.current.author || ""} ${asr.current.path || ""}`
            : `完成 ${asr?.done ?? 0} / ${asr?.total ?? 0}`}
        </p>
      </div>

      <section className="space-y-2">
        <h2 className="text-sm text-zinc-300">最近完成/失败</h2>
        <ul className="max-h-80 space-y-1 overflow-auto text-xs">
          {(q?.recent || []).map((j) => (
            <li key={j.id} className="rounded border border-zinc-800 px-2 py-1.5">
              <div className="flex justify-between">
                <span className="text-zinc-400">
                  [{j.kind}] {j.author} {j.target.slice(0, 28)}
                </span>
                <span
                  className={
                    j.status === "done"
                      ? "text-emerald-400"
                      : j.status === "failed"
                        ? "text-red-400"
                        : "text-zinc-500"
                  }
                >
                  {j.status}
                </span>
              </div>
              {j.message && (
                <p className="text-zinc-600 line-clamp-1">{j.message}</p>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
